import { defineNuxtPlugin } from 'nuxt/app'

import { APP_NAMES, setAppType, type AppType } from '../composables/useGlobal'

// Auto-detect and initialize app type based on current app context
// This replaces per-app init-app.ts files with a unified solution
export default defineNuxtPlugin(() => {
  try {
    // Auto-detect app type from Nuxt app context
    const appType = detectAppType()

    setAppType(appType)
    console.log(`[Init-App] Auto-detected and set app type to: ${appType}`)
  } catch (error) {
    console.warn('[Init-App] Failed to detect app type, defaulting to clientportal:', error)
    setAppType(APP_NAMES.CLIENTPORTAL)
  }
})

/**
 * Auto-detect app type based on APP_NAME environment variable set by build script
 * Similar pattern to getEnvironment() in config/index.ts
 *
 * On client-side: reads from window.__NUXT__.config.public.appName (runtime config from build)
 * On server-side: reads from process.env.APP_NAME (set by scripts/dev.js or build scripts)
 *
 * Falls back to CLIENTPORTAL if not set
 */
function detectAppType(): AppType {
  // Valid app names for validation
  const validAppNames = Object.values(APP_NAMES)

  // Default fallback
  let appType: AppType = APP_NAMES.CLIENTPORTAL

  // On client-side, read from window.__NUXT__ first (runtime config from build)
  // This is the authoritative source on the client as it contains the value baked into the build
  if (typeof window !== 'undefined') {
    try {
      // Read from runtimeConfig.public.appName (set in nuxt.config.ts)
      const nuxtConfig = (window as { __NUXT__?: { config?: { public?: { appName?: string } } } }).__NUXT__
      const windowAppName = nuxtConfig?.config?.public?.appName?.toUpperCase()

      // Only use windowAppName if it's a valid app name
      if (windowAppName && validAppNames.includes(windowAppName as AppType)) {
        return windowAppName as AppType
      }
    } catch {
      // Silently ignore errors reading from window.__NUXT__
    }
  }

  // On server-side, read from process.env.APP_NAME
  // This is set by scripts/dev.js during development or by build/runtime scripts in production

  if (typeof process !== 'undefined' && process.env?.APP_NAME) {
    const processAppName = process.env.APP_NAME.toUpperCase()

    if (validAppNames.includes(processAppName as AppType)) {
      appType = processAppName as AppType
    }
  }

  return appType
}
